import React, { useState } from "react";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Space, Row, Form, Badge, TextArea } from "@masterteam/mt-design-react";

import Upload from "../Upload";
import useIsAr from "../../Hooks/useIsAr";
import { addToast, formatDate } from "../../helpers";
import api from "../../providers/api";

import styles from "./UpdateKpiModal.module.scss";

const { Field } = Form;

function UpdateForm({
  item,
  index,
  isSemiAuto,
  isPercent,
  error,
  setFieldValue,
  values,
  hasNoBaseline,
  kpi
}) {
  const [isAr] = useIsAr();
  const [calculating, setCalculating] = useState(false);
  const [supportedExtensions] = useSelector(({ theme }) => {
    return [theme.DocumentType?.item?.Items?.map((item) => item.Value)];
  });
  const intl = useIntl();

  const calculateTarget = async (parameters, measureIndex) => {
    if (
      parameters.reduce(
        (acc, param) => acc && !isNaN(parseFloat(param.Value)),
        true
      )
    ) {
      setCalculating(true);

      try {
        const res = await api.kpi.calculateUpdateValue(parameters);
        setFieldValue(`measures[${measureIndex}].Value`, res.Data);
      } catch (error) {
        addToast.error(error?.message, {
          toastId: "update-error"
        });
      } finally {
        setCalculating(false);
      }
    }
  };

  const getValidStatusUpdate = async (item, measureIndex) => {
    try {
      const res = await api.kpi.getValidStatusUpdate(
        kpi?.ID,
        item?.measure?.ID,
        {
          Value: item?.Value
        }
      );
      setFieldValue(`measures[${measureIndex}].IsMandatory`, !res.Data);
    } catch (error) {
      addToast.error(error?.message, {
        toastId: "update-error"
      });
    }
  };

  return (
    <Space direction="vertical" size="medium" className={styles.measureItem}>
      <Row size="large">
        <Field
          disabled={true}
          label={intl.formatMessage({
            id: "update-kpi.target"
          })}
          hideErrorMessage
          inputProps={{
            value: item.measure.Target
              ? `${item.measure.Target}${item.measure.MaxTarget ? " - " : " "}${
                  item.measure.MaxTarget ? item.measure.MaxTarget : ""
                }${
                  isPercent
                    ? " %"
                    : isAr
                    ? kpi.ArabicUnitDetails
                    : kpi.EnglishUnitDetails
                }`
              : ""
          }}
        />

        <Field
          disabled={true}
          label={intl.formatMessage({
            id: "common.due-date"
          })}
          hideErrorMessage
          inputProps={{
            value: moment(item.measure.DueDate)
              .locale(isAr ? "ar" : "en")
              .format(isAr ? "YYYY/MM/DD" : "DD/MM/YYYY")
          }}
        />

        <Field
          type="checkbox"
          name={`measures[${index}].IsSkipped`}
          label={intl.formatMessage({
            id: "common.skip"
          })}
          inputProps={{
            highContrast: true,
            onChange: (val) => {
              setFieldValue(`measures[${index}].IsSkipped`, !item?.IsSkipped);
            }
          }}
          hideErrorMessage
        />
      </Row>

      {kpi?.DataSource === "semi-auto" && (
        <Row justify="between" size="medium">
          <div>
            <span>
              <FormattedMessage id="kpi-info.calculation-formula" />
            </span>
            : {kpi?.Formula}
          </div>
        </Row>
      )}

      <Space size="medium" direction="vertical">
        {isSemiAuto ? (
          item.Parameters?.map((param, paramIndex) =>
            item.Parameters[paramIndex]?.IsConstant ? null : (
              <Field
                key={param.ID}
                type="number"
                label={param.ParameterName}
                name={`measures[${index}].Parameters[${paramIndex}].Value`}
                disabled={
                  item.Parameters[paramIndex]?.IsConstant || item?.IsSkipped
                }
                hideErrorMessage
                inputProps={{
                  value: item.Parameters[paramIndex]?.IsConstant
                    ? item.Parameters[paramIndex]?.ConstantValue
                    : item.Parameters[paramIndex]?.Value,
                  onKeyDown: (evt) => {
                    if (evt.key === "e" || evt.key === "-" || evt.key === "+") {
                      evt.preventDefault();
                    }
                  },
                  min: 0,
                  step: "any",
                  error:
                    error?.Parameters?.[paramIndex]?.Value && !item?.IsSkipped,
                  onBlur: () => calculateTarget(item.Parameters, index),
                  onChange: (event) => {
                    setFieldValue(
                      `measures[${index}].Parameters[${paramIndex}].Value`,
                      event.target.value
                    );
                  }
                }}
              />
            )
          )
        ) : (
          <Field
            type="number"
            label={
              <FormattedMessage
                id="kpi-info.kpi-value"
                defaultMessage="kpi-info.kpi-value"
              />
            }
            name={`measures[${index}].Value`}
            disabled={item?.IsSkipped}
            hideErrorMessage
            inputProps={{
              value: item.Value,
              onKeyDown: (evt) => {
                if (evt.key === "e" || evt.key === "-" || evt.key === "+") {
                  evt.preventDefault();
                }
              },
              min: 0,
              step: "any",
              error: error?.Value && !item?.IsSkipped,
              onBlur: () => getValidStatusUpdate(item, index),
              onChange: (event) => {
                const value = event.target.value;
                setFieldValue(`measures[${index}].Value`, value);
                setFieldValue(
                  `measures[${index}].IsRequiredValue`,
                  value ? true : false
                );

                if (hasNoBaseline) {
                  values.measures.forEach((item, itemIndex) => {
                    if (itemIndex > index) {
                      setFieldValue(`measures[${itemIndex}]`, {
                        ...item,
                        IsRequiredValue: value ? true : false
                      });
                    }
                  });
                }
              }
            }}
          />
        )}
        {isSemiAuto && (
          <div
            onClick={() => {
              calculateTarget(item.Parameters, index);
            }}
          >
            <Badge
              fill="outline"
              loading={calculating}
              variant={error?.Value ? "error" : "primary"}
              size="large"
              block
            >
              <FormattedMessage
                id="kpi-info.kpi-value"
                defaultMessage="kpi-info.kpi-value"
              />
              {item.Value && (
                <strong>
                  {" "}
                  {item.Value?.toFixed(2)}
                  {isPercent
                    ? " %"
                    : isAr
                    ? kpi.ArabicUnitDetails
                    : kpi.EnglishUnitDetails}
                </strong>
              )}
            </Badge>
          </div>
        )}
        <Space direction="vertical">
          <label>
            <FormattedMessage id="kpi-details.justifcations" />
          </label>
          <TextArea
            placeholder={intl.formatMessage({
              id: "kpi-details.justifcations-placeholder"
            })}
            value={item?.Justification}
            onChange={(e) =>
              setFieldValue(
                `measures[${index}].Justification`,
                e.currentTarget.value
              )
            }
            error={
              item?.IsMandatory && !item?.IsSkipped
                ? !item?.Justification?.trim()?.length
                : false
            }
            disabled={item?.IsSkipped}
          />
        </Space>
        <Space direction="vertical">
          <label>
            <FormattedMessage id="kpi-details.corrective-action" />
          </label>
          <TextArea
            placeholder={intl.formatMessage({
              id: "kpi-details.corrective-action-placeholder"
            })}
            value={item?.CorrectiveAction}
            onChange={(e) =>
              setFieldValue(
                `measures[${index}].CorrectiveAction`,
                e.currentTarget.value
              )
            }
            error={
              item?.IsMandatory && !item?.IsSkipped
                ? !item?.CorrectiveAction?.trim()?.length
                : false
            }
            disabled={item?.IsSkipped}
          />
        </Space>
        <Space direction="vertical">
          <label>
            <FormattedMessage id="kpi-details.more-information" />
          </label>
          <TextArea
            placeholder={intl.formatMessage({
              id: "kpi-details.more-information-placeholder"
            })}
            value={item?.MoreInformation}
            // error={!item?.MoreInformation?.trim()?.length}
            error={error?.MoreInformation}
            onChange={(e) =>
              setFieldValue(
                `measures[${index}].MoreInformation`,
                e.currentTarget.value
              )
            }
            disabled={item?.IsSkipped}
          />
        </Space>
        <Upload
          name={`measures[${index}].Attachments`}
          error={error?.Attachments}
          label={<FormattedMessage id="common.attachment" />}
          onChange={(files) => {
            setFieldValue(`measures[${index}].Attachments`, files);
          }}
          multiple
          onDelete={(files) =>
            setFieldValue(`measures[${index}].Attachments`, files)
          }
          attachments={item.Attachments}
          accept={{
            "image/*": supportedExtensions.map((item) => `.${item}`),
            "audio/*": supportedExtensions.map((item) => `.${item}`),
            "video/*": supportedExtensions.map((item) => `.${item}`),
            "text/*": supportedExtensions.map((item) => `.${item}`),
            "font/*": supportedExtensions.map((item) => `.${item}`),
            "application/*": supportedExtensions.map((item) => `.${item}`)
          }}
          disabled={item?.IsSkipped}
        />
      </Space>
    </Space>
  );
}

export default UpdateForm;
